import React, { useEffect } from "react";
import Layout from "../../components/Layout";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Dialog,
  Fab,
  Grid,
  Icon,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OverviewCard from "../../components/Cards/OverviewCard";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useDispatch, useSelector } from "react-redux";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { closeThreeSixtyView } from "../../components/Carousal/carousalSlice";
import { FormattedMessage } from "react-intl";
import Product360View from "../../components/Product360View";
import {
  useGetAllBodyTypesQuery,
  useGetAllStatusQuery,
  useGetCarByIdQuery,
} from "../../app/api/Apis";
import NoDataPlaceholder from "../../components/NoDataPlaceholder";
import imagePlaceholder from "../../assets/placeholder.jpg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { showLoader } from "../../utils/globalSlice";
import SecureLS from "secure-ls";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import SwiperCarousal from "../../components/Carousal/SwiperCarousal";
import { NoBackpackSharp } from "@mui/icons-material";
import FloatingActions from "../../components/FloatingActions";
import ErrorBoundary from "../../components/ErrorBoundary";
import SeoContent from "../Seo/SeoContent";
import AedPrice from "../../components/AedPrice";

const CarDetail = () => {
  const ls = new SecureLS({ encodingType: "aes" }); // initialize secure ls

  const [searchParams, setSearchParams] = useSearchParams();
  const carId = searchParams.get("carId");
  let { state } = useLocation();

  // get language from redux store
  const lang = useSelector((state) => state.global.language);
  const isMob = useSelector((state) => state.global.isMobile);

  //get fullscreen state from redux store to handle 360 degree fullscreen
  const isFullScreen = useSelector((state) => state.carousal.fullscreen);

  //get 360 state from store to handle show and hide of 360 degree image
  const isThreeSixty = useSelector((state) => state.carousal.showThreeSixty);

  //get token from store
  const token = useSelector((state) => state.global.token);

  const dispatch = useDispatch();

  //function to handle hide 360 view
  const handleThreeSixtyClose = () => {
    dispatch(closeThreeSixtyView());
  };

  // payload for get car by id query
  const payload = {
    carUUID: carId ? carId : state.carId,
  };

  //use query hook to get all statuses
  const {
    isError: statusError,
    isLoading: statusLoading,
    data: carStatus,
  } = useGetAllStatusQuery();

  //use query hook to get all body types
  const {
    isError: bodyTypeError,
    isLoading: bodyTypeLoading,
    data: bodyTypes,
  } = useGetAllBodyTypesQuery();

  //query to fetch car by given id, refetches when change in args and skips whe token or carId is not available
  const {
    isLoading: carLoading,
    isError: carError,
    data: carData,
    isSuccess: carSuccess,
  } = useGetCarByIdQuery(payload, {
    refetchOnMountOrArgChange: true,
    // skip: !token,
  });

  // hook to handle  loading animation based on loading state of getCarById query
  useEffect(() => {
    if (carLoading) {
      dispatch(showLoader(true));

      return () => {
        dispatch(showLoader(false));
      };
    }
  }, [carLoading]);

  const getStatusTextEn = (statusId) => {
    const status = carStatus?.carStatusList?.find(
      (status) => status.carStatusId === statusId
    );
    return status ? status.statusTextEn : "Status not found";
  };
  const getBodyTypeTextEn = (bodyTypeId) => {
    const bodyType = bodyTypes?.carBodyList?.find(
      (bodyType) => bodyType.bodyTypeId === bodyTypeId
    );
    return bodyType ? bodyType.bodyTypeEn : "Status not found";
  };

  //define overview object to display the overviews from car query data
  const overview = {
    bodyTypeEn: getBodyTypeTextEn(carData?.usedCarEn?.bodyTypeId),
    yearOfManufacture: carData?.usedCarEn?.yearOfManufacture,
    mileage: carData?.usedCarEn?.mileage,
    warrantyTillDate: carData?.usedCarEn?.warrantyTillDate
      ? carData?.usedCarEn?.warrantyTillDate
      : carData?.usedCarEn?.warrantyNotesEn,
    numberOfDoors: carData?.usedCarEn?.numberOfDoors,
    color: carData?.usedCarEn?.colourEn,
    numberOfSeats: carData?.usedCarEn?.numberOfSeats,
    cylinderCapacity: carData?.usedCarEn?.cylinderCapacity || "",
  };

  const getCarImagesUrlArr = (imgData) => {
    return imgData.map((item) => item?.docName || "");
  };

  const getCarDefaultImageUrl = (arr) => {
    return arr?.find((item) => item?.defaultIMG === 1).docName;
  };

  return (
    <Layout>
      <SeoContent
        title={
          lang === "en"
            ? carData?.usedCarEn?.socialMediaTitleEn ??
              carData?.usedCarEn?.title
            : carData?.usedCarAr?.socialMediaTitleAr ??
              carData?.usedCarEn?.title
        }
        image={getCarDefaultImageUrl(carData?.images)}
        description={
          lang === "en"
            ? carData?.usedCarEn?.socialMediaDescEn ?? carData?.usedCarEn?.title
            : carData?.usedCarAr?.socialMediaDescAr ?? carData?.usedCarEn?.title
        }
        url={`https://usedcars.fazaa.ae/${carData?.usedCarEn?.carSlug}`}
        keywords={
          lang === "en"
            ? carData?.metaKeywords?.map((item) => item?.keywordEn)
            : carData?.metaKeywords?.map((item) => item?.keywordAr)
        }
      />
      {!carError && (
        <Container
          maxWidth="lg"
          sx={{ margin: "3rem auto", position: "relative" }}
        >
          <Stack
            direction={"row"}
            flexWrap={"wrap"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Breadcrumbs
              separator={
                lang === "en" ? (
                  <NavigateNextIcon fontSize="small" />
                ) : (
                  <NavigateBeforeIcon />
                )
              }
            >
              <Link href="/">
                <Icon>
                  <HomeIcon sx={{ color: "primary.main" }} />
                </Icon>
              </Link>
              {carSuccess ? (
                <Typography variant="body1">
                  {carData?.usedCarEn?.title}
                </Typography>
              ) : (
                <Skeleton variant="text" width={200} />
              )}
            </Breadcrumbs>
            <Typography
              variant="h1"
              sx={{ fontSize: "36px", color: "primary.main" }}
            >
              {carSuccess ? (
                carData?.usedCarEn?.carStatusId ? (
                  <Chip
                    sx={{ bgcolor: "success.main", color: "white.main" }}
                    label={getStatusTextEn(carData?.usedCarEn?.carStatusId)}
                    size="medium"
                  />
                ) : null
              ) : (
                <Skeleton
                  variant="rectangular"
                  width={100}
                  height={30}
                  sx={{ borderRadius: "100px" }}
                />
              )}
            </Typography>
          </Stack>

          <Grid container sx={{ justifyContent: "center", marginTop: "3rem" }}>
            <Grid item xs={12} md={12}>
              {/* {!!isThreeSixty ? <Product360View /> : <Carousal data={car} />} */}
              {carSuccess ? (
                carData?.images?.length > 0 ? (
                  // <Carousal data={carData} />
                  <SwiperCarousal
                    data={getCarImagesUrlArr(carData.images)}
                    heightMob="320"
                    heightDesk="640"
                    swiperOnCard={false}
                  />
                ) : (
                  // <SwiperExample carData={carData.images}/>
                  <Stack justifyContent="center">
                    <img src={imagePlaceholder} alt="fazaa used cars" />
                  </Stack>
                )
              ) : (
                <Skeleton variant="rectangular" width="100%" height={600} />
              )}
            </Grid>
          </Grid>
          <Grid container sx={{ marginTop: "4rem" }}>
            <Grid item xs={10} md={8}>
              {carSuccess ? (
                <Typography
                  variant="h1"
                  sx={{ fontSize: "32px", color: "primary.main" }}
                >
                  {carData?.usedCarEn?.title}
                </Typography>
              ) : (
                <Skeleton variant="text" width={400} height={75} />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", md: "flex-end" },
                justifyContent: "flex-start",
                marginTop: { xs: "1.5rem", md: "0" },
              }}
            >
              {carSuccess ? (
                // <Typography variant="h4" sx={{ color: "secondary.main" }}>
                //   AED {carData?.usedCarEn?.price.toLocaleString()}
                // </Typography>
                <AedPrice
                  price={carData?.usedCarEn?.price}
                  variant="h4"
                  color="secondary"
                />
              ) : (
                <Skeleton variant="text" width={600} height={40} />
              )}
              {carSuccess ? (
                <Typography
                  variant="h5"
                  sx={{ color: "secondary.main", direction: "ltr" }}
                >
                  {carData?.usedCarEn?.mileage.toLocaleString()} Km
                </Typography>
              ) : (
                <Skeleton variant="text" width={500} />
              )}
            </Grid>
          </Grid>
          <Grid container sx={{ marginTop: "3rem" }}>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ fontWeight: "700" }}>
                <FormattedMessage id="app.overview.title" />
              </Typography>
            </Grid>
          </Grid>
          {/* <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            useFlexGap
            sx={{ marginTop: "3rem" }}
            flexWrap
          > */}
          <Grid
            container
            sx={{ marginTop: "3rem" }}
            spacing={1}
            justifyContent={{ md: "center" }}
          >
            {carSuccess
              ? Object.entries(overview).map(
                  ([key, value], index) =>
                    (value !== "" || value !== null) && (
                      <Grid item xs={6} md={1.5} key={index}>
                        <OverviewCard
                          objKey={key}
                          objValue={value}
                          elevation={0}
                          cardVariant="outlined"
                          cardPadding="auto"
                          label={
                            <FormattedMessage id={`app.overview.${key}`} />
                          }
                        />
                      </Grid>
                    )
                )
              : [...Array(7).keys()].map((item, index) => (
                  <Skeleton
                    variant="rectangular"
                    width={200}
                    height={100}
                    key={index}
                  />
                ))}
          </Grid>
          {/* </Stack> */}

          {/* <Grid container spacing={2} sx={{ marginTop: "2rem" }}>
            {carSuccess
              ? Object.entries(overview).map(([key, value], index) => (
                  <Grid item xs={6} md={2} key={`${key}-${index}`}>
                    <OverviewCard
                      objKey={key}
                      objValue={value}
                      elevation={0}
                      cardVariant="outlined"
                      cardPadding="auto"
                      label={<FormattedMessage id={`app.overview.${key}`} />}
                    />
                  </Grid>
                ))
              : [...Array(5).keys()].map((item, index) => (
                  <Grid item xs={6} md={2} key={index}>
                    <Skeleton variant="rectangular" width={200} height={100} />
                  </Grid>
                ))}
          </Grid> */}

          <Grid container sx={{ marginTop: "3rem" }}>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ fontWeight: "700" }}>
                <FormattedMessage id="app.features.title" />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "2rem" }}>
              {carSuccess ? (
                <List>
                  <Grid container>
                    {carData?.specification?.map((item, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <ListItem
                          key={index}
                          disablePadding
                          sx={{ textAlign: lang === "en" ? "left" : "right" }}
                        >
                          <ListItemIcon>
                            {lang === "en" ? (
                              <ArrowForwardIosIcon color="secondary" />
                            ) : (
                              <ArrowBackIosNewIcon color="secondary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              lang === "en"
                                ? item?.specTextEn
                                : item?.specTextAr
                            }
                            sx={{ textTransform: "capitalize" }}
                          />
                        </ListItem>
                      </Grid>
                    ))}
                  </Grid>
                </List>
              ) : (
                [...Array(6).keys()].map((item, index) => (
                  <Skeleton variant="text" width={300} key={index} />
                ))
              )}
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "3rem" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "700", textTransform: "capitalize" }}
              >
                <FormattedMessage id="app.details.warrantyService" />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "2rem" }}>
              <Stack direction={"column"} spacing={3} useFlexGap>
                <Stack direction={"column"}>
                  {carSuccess ? (
                    carData?.usedCarEn?.warrantyTillDate && (
                      <Typography
                        variant="body1"
                        sx={{ "& span": { fontWeight: "bold" } }}
                        gutterBottom
                      >
                        <span>
                          <FormattedMessage id="app.warranty.tillDate" />
                        </span>{" "}
                        {carData?.usedCarEn?.warrantyTillDate}
                      </Typography>
                    )
                  ) : (
                    <Skeleton variant="text" width={250} height={30} />
                  )}
                  {carSuccess ? (
                    carData?.usedCarEn?.warrantyTillKM && (
                      <Typography
                        variant="body1"
                        sx={{ "& span": { fontWeight: "bold" } }}
                        gutterBottom
                      >
                        <span>
                          <FormattedMessage id="app.warranty.tillKm" />
                        </span>{" "}
                        {carData?.usedCarEn?.warrantyTillKM} Km
                      </Typography>
                    )
                  ) : (
                    <Skeleton variant="text" width={250} height={30} />
                  )}
                  {(carData?.usedCarEn?.warrantyTillKM === "" ||
                    (carData?.usedCarEn?.warrantyTillKM === null &&
                      carData?.usedCarEn?.warrantyTillDate === "") ||
                    carData?.usedCarEn?.warrantyTillDate === null) && (
                    <Typography
                      variant="body1"
                      sx={{ "& span": { fontWeight: "bold" } }}
                      gutterBottom
                    >
                      <span>
                        <FormattedMessage id="app.warranty.notes" />
                      </span>{" "}
                      {carData?.usedCarEn?.warrantyNotesEn}
                    </Typography>
                  )}
                </Stack>
                <Stack direction={"column"}>
                  {carSuccess ? (
                    carData?.usedCarEn?.serviceTillDate && (
                      <Typography
                        variant="body1"
                        sx={{ "& span": { fontWeight: "bold" } }}
                        gutterBottom
                      >
                        <span>
                          <FormattedMessage id="app.service.tillDate" />
                        </span>{" "}
                        {carData?.usedCarEn?.serviceTillDate}
                      </Typography>
                    )
                  ) : (
                    <Skeleton variant="text" width={250} height={30} />
                  )}
                  {carSuccess ? (
                    carData?.usedCarEn?.serviceTillKM && (
                      <Typography
                        variant="body1"
                        sx={{ "& span": { fontWeight: "bold" } }}
                        gutterBottom
                      >
                        <span>
                          <FormattedMessage id="app.service.tillKm" />
                        </span>{" "}
                        {carData?.usedCarEn?.serviceTillKM} Km
                      </Typography>
                    )
                  ) : (
                    <Skeleton variant="text" width={250} height={30} />
                  )}
                  {(carData?.usedCarEn?.serviceTillKM === "" ||
                    (carData?.usedCarEn?.serviceTillKM === null &&
                      carData?.usedCarEn?.serviceTillDate === "") ||
                    carData?.usedCarEn?.serviceTillDate === null) && (
                    <Typography
                      variant="body1"
                      sx={{ "& span": { fontWeight: "bold" } }}
                      gutterBottom
                    >
                      <span>
                        <FormattedMessage id="app.service.notes" />
                      </span>{" "}
                      {carData?.usedCarEn?.serviceNotesEn}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Grid>
            {(carData?.usedCarEn?.contactInfoEn ||
              carData?.usedCarEn?.contactInfoAr) && (
              <Grid item xs={12} sx={{ marginTop: "3rem" }}>
                <Typography variant="h5" sx={{ fontWeight: "700" }}>
                  <FormattedMessage id="app.title.contact" />
                </Typography>
              </Grid>
            )}
            {lang === "en" && carData?.usedCarEn?.contactInfoEn && (
              <Grid item xs={12} md={4} sx={{ marginTop: "2rem" }}>
                <Typography variant="body1">
                  {carData?.usedCarEn?.contactInfoEn}
                </Typography>
              </Grid>
            )}
            {lang === "ar" && carData?.usedCarAr?.contactInfoAr && (
              <Grid item xs={12} md={4} sx={{ marginTop: "2rem" }}>
                <Typography variant="body1">
                  {carData?.usedCarAr?.contactInfoAr}
                </Typography>
              </Grid>
            )}
          </Grid>
          <FloatingActions carData={carData} />
        </Container>
      )}

      {carError && (
        <Container
          maxWidth="xl"
          sx={{ margin: "5rem auto", minHeight: "30vh" }}
        >
          <NoDataPlaceholder message="Something went wrong while loading the car information. Please try again later!!" />
        </Container>
      )}

      <Dialog
        maxWidth="xl"
        sx={{ maxHeight: "100%" }}
        fullScreen={!!isFullScreen}
        open={!!isThreeSixty}
        onClose={handleThreeSixtyClose}
      >
        <Product360View />
      </Dialog>
    </Layout>
  );
};

export default CarDetail;
